import { localStorageGet } from '@Infra/LocalStorage';
import { LOCAL_STORAGE_KEYS } from '@Infra/LocalStorage/keys';

/**
 * A minimum plausibility bound, not a mirrored policy: the actual device retention (currently
 * fifteen days) is served by the API and never hard-coded here. This is the same floor the server
 * imposes on itself on `RECORDING_DEVICE_RETENTION_MS` (one day, `@Min(86_400_000)`), for the same
 * reason — a persisted value below it can only be corruption (a tampered store, a partial write, a
 * leftover value from an earlier format), never a legitimate policy.
 */
const MINIMUM_PLAUSIBLE_RETENTION_MS = 86_400_000;

/**
 * Synchronous, not a thunk: `OutboxRunnerHost` needs the retention granted by the last successful
 * fetch of the recording policy at the same tick it starts the synchronisation engine, so the
 * engine never runs a single session without a retention it can enforce offline. Anything that is
 * not a well-formed integer of at least `MINIMUM_PLAUSIBLE_RETENTION_MS` — no value yet, malformed
 * content, a negative or zero figure, a leftover corrupted string — resolves to `null`, which the
 * caller always reads as "no retention known yet, never expire", never as a value to enforce. This
 * fails on the safe side: nothing is ever erased too early.
 */
const readDeviceRetention = (): number | null => {
	const rawRetentionMs = localStorageGet(LOCAL_STORAGE_KEYS.DEVICE_RETENTION_MS);

	if (rawRetentionMs === null) {
		return null;
	}

	const parsedRetentionMs = Number(rawRetentionMs);

	if (!Number.isInteger(parsedRetentionMs) || parsedRetentionMs < MINIMUM_PLAUSIBLE_RETENTION_MS) {
		return null;
	}

	return parsedRetentionMs;
};

export default readDeviceRetention;
